import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaRoad, 
  FaClock, 
  FaLayerGroup, 
  FaBookOpen, 
  FaVideo, 
  FaLaptopCode, 
  FaTrophy,
  FaBriefcase,
  FaChevronDown,
  FaChevronUp,
  FaCalendarWeek,
  FaCheckCircle,
  FaLightbulb,
  FaExclamationCircle,
  FaArrowRight,
  FaBook,
  FaGraduationCap,
  FaFile
} from 'react-icons/fa';
import { TbTargetArrow, TbBrain } from 'react-icons/tb';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { generateRoadmap } from '../services/roadmapService';
import { getMe } from '../services/authService';
import { generateRoadmapHTML } from '../utils/htmlGenerator';

const Roadmap = () => {
  const [topic, setTopic] = useState('');
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [usageCount, setUsageCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Fetch user usage count
    const fetchUsage = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const data = await getMe();
        if (data.success) {
            setUsageCount(data.user.roadmapGenCount || 0);
            setIsAdmin(data.user.isAdmin || false);
        }
      } catch (err) {
        console.error("Failed to fetch usage stats", err);
      }
    };
    fetchUsage();
  }, []);

  const loadingMessages = [
    'Analyzing career path...',
    'Mapping skills and milestones...',
    'Curating learning resources...',
    'Designing your master project...',
    'Finalizing your roadmap...'
  ];

  // Rotate loader messages
  useEffect(() => {
    let intervalId;
    if (loading) {
      intervalId = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2000);
    } else {
      setLoadingMessageIndex(0);
    }
    return () => clearInterval(intervalId);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRoadmap(null);
    setExpandedPhases({});
    setExpandedTopics({});
    setExpandedWeeks({});

    try {
      const data = await generateRoadmap({
        topic,
        duration: '6 Months',
        level: 'Intermediate'
      });
      
      setRoadmap(data);
      // Auto-expand the first phase
      if (data.roadmap && data.roadmap.length > 0) {
          setExpandedPhases({ 0: true });
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to generate roadmap. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = (index) => {
    setExpandedPhases(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleTopic = (phaseIdx, skillIdx, topicIdx) => {
    const key = `${phaseIdx}-${skillIdx}-${topicIdx}`;
    setExpandedTopics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleWeek = (phaseIdx, weekIdx) => {
    const key = `${phaseIdx}-${weekIdx}`;
    setExpandedWeeks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const downloadHTML = () => {
    if (!roadmap) return;

    const htmlContent = generateRoadmapHTML(roadmap);

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${roadmap.role.toLowerCase().replace(/\s+/g, '-')}-interactive.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
      `
    }}>
      {/* Top Decoration */}
      <div className="h-[40rem] bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
         <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          
          
          {/* Header Section */}
          <div className="text-center mb-12 animate-fade-in-up relative">
                     
                  
          </div>

          {/* Input Form */}
          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-8 mb-12 border border-white/50 relative overflow-hidden" style={{ animationDelay: '0.1s' }}>
             <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
             
             {/* Usage Limit Badge */}
             <div className="absolute top-6 right-6 z-20">
                 <div className={`backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs font-bold border flex items-center shadow-sm ${
                    isAdmin 
                    ? 'bg-amber-50 border-amber-100 text-amber-600' 
                    : 'bg-indigo-50 border-indigo-100 text-indigo-600'
                 }`}>
                      {isAdmin ? <FaTrophy className="mr-1.5" /> : <FaClock className="mr-1.5" />}
                      {isAdmin ? 'Unlimited Access' : `${usageCount}/3 Free Generations`}
                  </div>
             </div>
             
             <div className="relative z-10">
                <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                    <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white mr-4 shadow-lg shadow-indigo-500/30">
                        <TbTargetArrow className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="heading-tertiary text-gray-900">What do you want to learn?</h2>
                        <p className="text-gray-500 font-medium">Enter a role or skill to generate your path</p>
                    </div>
                    </div>

                    <div>
                    <label htmlFor="topic" className="block text-sm font-bold text-gray-700 mb-2">
                        Target Role / Skill
                    </label>
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaBriefcase className="text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                        </div>
                        <input
                        type="text"
                        id="topic"
                        required
                        className="w-full pl-11 px-4 py-4 bg-white border border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 transition-all font-medium text-lg text-gray-900 placeholder-gray-400 shadow-sm"
                        placeholder="e.g. Frontend Developer, Data Scientist, React"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        />
                    </div>
                    <p className="text-sm text-gray-500 mt-3 ml-1 flex items-center">
                        <span className="bg-yellow-100 text-yellow-700 text-[10px] font-bold px-2 py-0.5 rounded mr-2 uppercase">Tip</span>
                        Be specific (e.g. "Full Stack Developer" instead of just "Developer")
                    </p>
                    </div>
                </div>

                <div className="pt-2">
                    <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-primary-600 text-white rounded-xl font-bold text-lg shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden"
                    >
                         <span className="relative z-10 flex items-center justify-center">
                            Generate Personalized Roadmap
                            <FaRoad className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </span>
                         <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    </button>
                </div>
                </form>

                {/* Error Message */}
                {error && (
                <div className="mt-8 p-4 bg-red-50 border border-red-100 rounded-xl animate-shake flex items-center">
                    <div className="w-8 h-8 bg-red-100 text-red-500 rounded-lg flex items-center justify-center mr-3 shrink-0">
                        <span className="font-bold">!</span>
                    </div>
                    <p className="text-red-700 font-bold">{error}</p>
                </div>
                )}
             </div>
          </div>

          {/* Roadmap Display */}
          <AnimatePresence>
            {roadmap && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-12 space-y-8"
              >
                <div className="flex justify-end">
                   <button 
                      onClick={downloadHTML}
                      className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 hover:text-primary-600 transition-colors shadow-sm flex items-center"
                   >
                      <FaBriefcase className="mr-2" />
                      Export Interactive View
                   </button>
                </div>

                <div className="bg-white/90 backdrop-blur-md p-6 sm:p-10 rounded-3xl shadow-xl border border-white/50">
                  <div className="text-center mb-12">
                    <h2 className="heading-secondary text-gray-900 capitalize mb-4">{roadmap.role}</h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">{roadmap.description}</p>
                    <div className="mt-6 flex justify-center space-x-4">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                        <FaClock className="mr-2" /> {roadmap.estimatedDuration}
                      </span>
                    </div>
                  </div>

                  <div className="relative max-w-4xl mx-auto">
                  <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
                  {roadmap.roadmap && roadmap.roadmap.map((phase, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="mb-8 relative pl-12 md:pl-24"
                    >
                      {/* Phase Connector Dot */}
                      <div className="absolute left-4 md:left-8 -translate-x-1/2 top-8 w-5 h-5 rounded-full bg-indigo-600 border-4 border-white shadow-md z-10"></div>
                      
                      <div className="group bg-white rounded-2xl transition-all duration-300 shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden">
                        <div 
                          className="p-6 cursor-pointer hover:bg-gray-50/50 transition-colors"
                          onClick={() => togglePhase(index)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-start gap-4">
                               <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-black text-sm shrink-0 mt-1 shadow-sm">
                                  {index + 1}
                               </div>
                               <div>
                                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{phase.phase}</h3>
                                  <div className="flex flex-wrap items-center gap-2 mt-2 text-sm font-medium text-gray-500">
                                     <span className="px-2.5 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs uppercase tracking-wide">{phase.level}</span>
                                     <span>•</span>
                                     <span className="flex items-center"><FaClock className="mr-1.5 text-xs" /> {phase.duration}</span>
                                  </div>
                               </div>
                            </div>
                            <div className="ml-4 flex flex-col items-end gap-2">
                               <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all ${expandedPhases[index] ? 'rotate-180' : ''}`}>
                                   <FaChevronDown />
                               </div>
                            </div>
                          </div>
                          
                          {/* Desktop Outcome Badge - Visible when collapsed or expanded */}
                           <div className="mt-4 ml-12 hidden sm:flex items-center text-sm font-bold text-emerald-700 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 self-start w-fit">
                              <FaTrophy className="mr-2 text-emerald-500" />
                              <span className="truncate max-w-xs md:max-w-md">{phase.outcome}</span>
                           </div>
                        </div>

                        <AnimatePresence>
                          {expandedPhases[index] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="px-6 pb-8 pt-2 border-t border-gray-100 bg-gray-50/50">
                                 {/* Mobile Outcome Badge */}
                                 <div className="sm:hidden mb-6 flex items-start text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-2 rounded-xl border border-emerald-100">
                                    <FaTrophy className="mr-2 mt-0.5 text-emerald-500 shrink-0" />
                                    <span>{phase.outcome}</span>
                                 </div>

                                 <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center mt-4">
                                    <TbBrain className="mr-2 text-lg text-gray-500" /> Skills & Detailed Topics
                                 </h4>

                                 <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-8">
                                     {phase.skills.map((skill, sIdx) => (
                                         <div key={sIdx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow hover:border-indigo-100">
                                             <div className="flex items-center mb-3">
                                                 <div className="w-6 h-6 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center mr-2">
                                                     <FaBookOpen size={10} />
                                                 </div>
                                                 <h5 className="font-bold text-gray-900 capitalize text-sm">{skill.name}</h5>
                                             </div>
                                             <ul className="space-y-2">
                                                  {Array.isArray(skill.topics) && skill.topics.map((topic, tIdx) => {
                                                      const isTopicExpanded = expandedTopics[`${index}-${sIdx}-${tIdx}`];
                                                      const isObject = typeof topic === 'object';
                                                      const hasDetails = isObject && (topic.description || (topic.subtopics && topic.subtopics.length > 0));

                                                      return (
                                                          <li key={tIdx} className="text-sm text-gray-600 flex flex-col leading-relaxed mb-2">
                                                              <div 
                                                                  className={`flex items-start ${hasDetails ? 'cursor-pointer group select-none' : ''}`}
                                                                  onClick={() => hasDetails && toggleTopic(index, sIdx, tIdx)}
                                                              >
                                                                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mr-2 mt-1.5 shrink-0"></span>
                                                                  <div className="flex-1 flex justify-between items-start">
                                                                       <span className={`font-medium ${hasDetails ? 'text-indigo-900 group-hover:text-indigo-600 transition-colors' : 'text-gray-800'}`}>
                                                                          {isObject ? topic.title : topic}
                                                                       </span>
                                                                       {hasDetails && (
                                                                           <div className="p-1 -mt-1 -mr-1 rounded-full group-hover:bg-indigo-50 transition-colors">
                                                                                <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isTopicExpanded ? 'rotate-180 text-indigo-500' : ''}`} />
                                                                           </div>
                                                                       )}
                                                                  </div>
                                                              </div>
                                                              
                                                              <AnimatePresence>
                                                                  {hasDetails && isTopicExpanded && (
                                                                       <motion.div
                                                                          initial={{ height: 0, opacity: 0 }}
                                                                          animate={{ height: "auto", opacity: 1 }}
                                                                          exit={{ height: 0, opacity: 0 }}
                                                                          transition={{ duration: 0.2 }}
                                                                          className="overflow-hidden"
                                                                       >
                                                                          {topic.description && (
                                                                              <p className="text-xs text-gray-500 ml-3.5 mt-1 mb-2">{topic.description}</p>
                                                                          )}
                                                                          {topic.subtopics && topic.subtopics.length > 0 && (
                                                                              <ul className="ml-3.5 space-y-1 border-l-2 border-indigo-100 pl-2 mb-2">
                                                                                  {topic.subtopics.map((sub, subIdx) => (
                                                                                      <li key={subIdx} className="text-xs text-gray-600 flex items-start">
                                                                                          <FaArrowRight className="w-2 h-2 text-indigo-400 mr-1.5 mt-0.5 shrink-0" />
                                                                                          {sub}
                                                                                      </li>
                                                                                  ))}
                                                                              </ul>
                                                                          )}
                                                                       </motion.div>
                                                                  )}
                                                              </AnimatePresence>
                                                          </li>
                                                      );
                                                  })}
                                             </ul>
                                         </div>
                                     ))}
                                 </div>
                                 
                                 {/* Weekly Breakdown Section */}
                                  {phase.weeklyBreakdown && phase.weeklyBreakdown.length > 0 && (
                                      <div className="mb-8">
                                          <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center">
                                              <FaCalendarWeek className="mr-2 text-lg text-gray-500" /> Weekly Breakdown
                                          </h4>
                                          <div className="space-y-4">
                                              {phase.weeklyBreakdown.map((week, wIdx) => {
                                                  const isWeekExpanded = expandedWeeks[`${index}-${wIdx}`];
                                                  return (
                                                      <div key={wIdx} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 overflow-hidden transition-all duration-300">
                                                          <div 
                                                              className="p-5 flex flex-wrap items-center justify-between cursor-pointer hover:bg-white/40 transition-colors"
                                                              onClick={() => toggleWeek(index, wIdx)}
                                                          >
                                                              <div className="flex items-center gap-3">
                                                                  <div className="w-10 h-10 rounded-full bg-white border-2 border-indigo-500 text-indigo-600 flex items-center justify-center font-bold shadow-sm shrink-0">
                                                                      W{week.week}
                                                                  </div>
                                                                  <div className="flex-1">
                                                                      <h5 className="font-bold text-gray-900 leading-tight">{week.focus}</h5>
                                                                      <p className="text-xs text-gray-500 flex items-center mt-1">
                                                                          <FaClock className="mr-1" /> {week.hoursRequired}
                                                                      </p>
                                                                  </div>
                                                              </div>
                                                              <div className={`p-2 rounded-full transition-transform duration-200 ${isWeekExpanded ? 'rotate-180 bg-indigo-100' : ''}`}>
                                                                    <FaChevronDown className="w-4 h-4 text-indigo-500" />
                                                              </div>
                                                          </div>
                                                          
                                                          <AnimatePresence>
                                                              {isWeekExpanded && (
                                                                  <motion.div
                                                                      initial={{ height: 0, opacity: 0 }}
                                                                      animate={{ height: "auto", opacity: 1 }}
                                                                      exit={{ height: 0, opacity: 0 }}
                                                                      transition={{ duration: 0.3 }}
                                                                  >
                                                                      <div className="px-5 pb-5 pl-9 border-l-2 border-indigo-200 ml-5 mb-5 mr-5">
                                                                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Tasks to Complete:</p>
                                                                          <ul className="space-y-2 mb-4">
                                                                              {week.tasks && week.tasks.map((task, taskIdx) => (
                                                                                  <li key={taskIdx} className="text-sm text-gray-700 flex items-start">
                                                                                      <FaCheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 shrink-0" />
                                                                                      <span>{task}</span>
                                                                                  </li>
                                                                              ))}
                                                                          </ul>
                                                                          {week.milestone && (
                                                                              <div className="bg-white px-4 py-3 rounded-lg border border-green-200 flex items-start">
                                                                                  <FaTrophy className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 shrink-0" />
                                                                                  <div>
                                                                                      <p className="text-xs font-semibold text-gray-500 uppercase">Milestone</p>
                                                                                      <p className="text-sm text-gray-800 font-medium">{week.milestone}</p>
                                                                                  </div>
                                                                              </div>
                                                                          )}
                                                                      </div>
                                                                  </motion.div>
                                                              )}
                                                          </AnimatePresence>
                                                      </div>
                                                  );
                                              })}
                                          </div>
                                      </div>
                                  )}

                                  {/* Action Items Section */}
                                  {phase.actionItems && phase.actionItems.length > 0 && (
                                      <div className="mb-8">
                                          <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center">
                                              <FaExclamationCircle className="mr-2 text-lg text-gray-500" /> What To Do Next
                                          </h4>
                                          <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                                              {phase.actionItems.map((item, aIdx) => (
                                                  <div key={aIdx} className={`p-4 rounded-xl border flex items-start ${item.priority === 'high' ? 'bg-red-50 border-red-200' : item.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 shrink-0 ${item.priority === 'high' ? 'bg-red-500' : item.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}`}>
                                                          <span className="text-white text-xs font-bold">{aIdx + 1}</span>
                                                      </div>
                                                      <div className="flex-1">
                                                          <div className="flex items-center justify-between mb-1">
                                                              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${item.priority === 'high' ? 'bg-red-200 text-red-700' : item.priority === 'medium' ? 'bg-yellow-200 text-yellow-700' : 'bg-green-200 text-green-700'}`}>{item.priority}</span>
                                                              {item.timeEstimate && <span className="text-xs text-gray-500 flex items-center"><FaClock className="mr-1" /> {item.timeEstimate}</span>}
                                                          </div>
                                                          <p className="text-sm text-gray-800 font-medium">{item.task}</p>
                                                      </div>
                                                  </div>
                                              ))}
                                          </div>
                                      </div>
                                  )}

                                  {/* Pro Tips Section */}
                                  {phase.tips && phase.tips.length > 0 && (
                                      <div className="mb-8">
                                          <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center">
                                              <HiOutlineLightBulb className="mr-2 text-lg text-gray-500" /> Pro Tips
                                          </h4>
                                          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-5 rounded-xl border border-amber-200">
                                              <ul className="space-y-3">
                                                  {phase.tips.map((tip, tipIdx) => (
                                                      <li key={tipIdx} className="flex items-start text-sm text-gray-700">
                                                          <FaLightbulb className="w-4 h-4 text-amber-500 mr-3 mt-0.5 shrink-0" />
                                                          <span>{tip}</span>
                                                      </li>
                                                  ))}
                                              </ul>
                                          </div>
                                      </div>
                                  )}
                                  
                                  {/* Projects Section */}
                                 {phase.projects && phase.projects.length > 0 && (
                                     <div className="mb-8">
                                         <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center">
                                             <FaLaptopCode className="mr-2 text-lg text-gray-500" />
                                             Practical Projects
                                         </h4>
                                         <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                             {phase.projects.map((project, pIdx) => (
                                                 <div key={pIdx} className="bg-gradient-to-br from-white to-indigo-50/50 rounded-xl p-5 border border-indigo-100 shadow-sm hover:shadow-md transition-all">
                                                     <h5 className="font-bold text-gray-900 mb-2">{project.name}</h5>
                                                     <p className="text-sm text-gray-600 leading-relaxed">{project.description}</p>
                                                 </div>
                                             ))}
                                         </div>
                                     </div>
                                 )}

                                 {/* Resources Section */}
                                 {phase.resources && phase.resources.length > 0 && (
                                     <div>
                                         <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center">
                                              <FaVideo className="mr-2 text-lg text-gray-500" />
                                              Recommended Resources
                                         </h4>
                                         <div className="flex flex-wrap gap-3">
                                             {phase.resources.map((resource, rIdx) => (
                                                 <a 
                                                     key={rIdx}
                                                     href={resource.url} 
                                                     target="_blank" 
                                                     rel="noopener noreferrer"
                                                     className="inline-flex items-center px-4 py-2 rounded-xl bg-white border border-gray-200 text-indigo-600 hover:text-white hover:bg-indigo-600 hover:border-indigo-600 hover:shadow-md transition-all text-sm font-bold group"
                                                 >
                                                     {resource.name}
                                                     <span className="ml-2 text-xs opacity-50 group-hover:opacity-100 transition-opacity">↗</span>
                                                 </a>
                                             ))}
                                         </div>
                                     </div>
                                 )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Master Project Section */}
                {roadmap.masterProject && (
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mt-16 bg-gradient-to-b from-[#1a1c2e] to-[#11121c] rounded-[2rem] shadow-2xl overflow-hidden text-white relative border border-white/5"
                    >
                        <div className="relative p-8 md:p-12 text-center z-10">
                            {/* Icon */}
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/5 backdrop-blur-sm mb-8 ring-1 ring-white/10 shadow-xl">
                                <FaTrophy className="text-4xl text-yellow-400" />
                            </div>
                            
                            {/* Title */}
                            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-white tracking-tight">
                                Master Capstone <br/> Project
                            </h2>
                            
                            {/* Separator */}
                            <div className="h-1.5 w-16 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto rounded-full mb-8"></div>
                            
                            {/* Project Name */}
                            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-white">{roadmap.masterProject.title}</h3>
                            
                            {/* Description */}
                            <p className="text-gray-300 text-lg mb-10 max-w-3xl mx-auto leading-relaxed">
                                {roadmap.masterProject.description}
                            </p>
                            
                            {/* Tech Stack Card */}
                            <div className="bg-[#1e2030]/50 rounded-2xl p-8 backdrop-blur-sm max-w-3xl mx-auto border border-white/5 shadow-inner">
                                <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-300 mb-6 flex items-center justify-center">
                                  <FaLayerGroup className="mr-2" />
                                  Tech Stack Required
                                </h4>
                                <div className="flex flex-wrap justify-center gap-3">
                                    {roadmap.masterProject.techStack.map((tech, idx) => (
                                        <span key={idx} className="px-5 py-2.5 bg-[#25283d] hover:bg-[#2d3045] transition-colors rounded-lg text-sm font-semibold border border-white/5 text-gray-200">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

       {/* Full Screen Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/60 backdrop-blur-md animate-in fade-in duration-300">
           <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center max-w-sm w-full mx-4">
              <div className="relative w-20 h-20 mb-6">
                   <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
                   <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                   <div className="absolute inset-0 flex items-center justify-center">
                       <FaRoad className="w-8 h-8 text-indigo-600 animate-pulse" />
                   </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Generating Path...</h3>
              <p className="text-gray-500 text-center font-medium animate-pulse">
                {loadingMessages[loadingMessageIndex]}
              </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roadmap;
